<template>
  <div>
    <BulkApproverEditorDialog
      :visible="isApproverEditorMode && visible"
      :saving="saving"
      :initial-tab="initialApproverTab"
      :users="allUsers"
      :preparer-ids="preparerIdsList"
      :approver-ids-from-tasks="approverUnionFromTasks"
      :sign-off-ids-from-tasks="signOffUnionFromTasks"
      :initial-approver-ids="approverIdsList"
      :initial-sign-off-ids="signOffIdsList"
      :location-ids="distinctLocationIds"
      :cycle-id="cycleId"
      test-id-prefix="tasklist"
      @update:visible="$emit('update:visible', $event)"
      @save="onApproverEditSave"
    />

    <BulkPreparerEditorDialog
      :visible="isPreparerEditorMode && visible"
      :saving="saving"
      :users="allUsers"
      :preparer-ids-from-tasks="preparerUnionFromTasks"
      :approver-ids-from-tasks="approverUnionFromTasks"
      :sign-off-ids-from-tasks="signOffUnionFromTasks"
      :initial-preparer-ids="preparerIdsList"
      :location-ids="distinctLocationIds"
      :cycle-id="cycleId"
      test-id-prefix="tasklist"
      @update:visible="$emit('update:visible', $event)"
      @save="onPreparerEditSave"
    />

    <lb-drawer
      v-if="!isApproverEditorMode && !isPreparerEditorMode && visible && bulkActionUserType"
      :value="visible"
      :heading="'Edit ' + bulkActionUserType"
      :width="drawerWidths.sm"
      data-testid="tasklist__bulk-action-user-drawer"
      @input="$emit('update:visible', $event)"
    >
      <template v-slot:body>
        <div class="d-flex align-center">
          <v-chip
            color="lightpurple"
            class="caccounting--text d-flex align-center w-30"
            data-testid="tasklist__bulk-action-user-drawer-ref-chip"
          >
            <v-icon class="mr-2">mdi-information-outline</v-icon>
            <span>{{ selectedTasks.length }} task item selected</span>
          </v-chip>
        </div>
        <p class="mb-1 mt-3 gray--text">Action</p>
        <lb-dropdown
          hidedetails
          label=""
          placeholder="Select action "
          :value="selectAction"
          :items="actionItems"
          itemtext="name"
          itemvalue="value"
          class="mt-2"
          width="10"
          :attach="false"
          data-testid="tasklist__bulk-action-user-drawer-action-dropdown"
          @input="$emit('update:selectAction', $event)"
          @change="$emit('action-change', $event)"
        />
        <lb-dropdown
          v-if="selectAction === 'remove' || selectAction === 'replace' || selectAction === 'move'"
          hidedetails
          label="select user"
          :value="bulkActionUserId"
          :items="validActiveUsers"
          itemtext="name"
          itemvalue="_id"
          class="mt-4"
          :attach="false"
          data-testid="tasklist__bulk-action-user-drawer-user-dropdown"
          item-class="noTransform"
          @input="$emit('update:bulkActionUserId', $event)"
        />

        <span
          v-if="selectAction === 'associate' || selectAction === 'replace'"
          class="gray--text"
        >Select {{ bulkActionUserType }}</span>
        <lb-dropdown
          v-if="selectAction === 'associate' || selectAction === 'replace'"
          hidedetails
          label=""
          :value="changeToUser"
          :items="availableUsers"
          itemtext="name"
          itemvalue="_id"
          class="mt-4"
          :attach="false"
          data-testid="tasklist__bulk-action-user-drawer-change-to-user-dropdown"
          @input="$emit('update:changeToUser', $event)"
        />

        <p v-if="selectAction === 'associate'" class="gray--text mt-2">
          Selected user will be added to all selected tasks
        </p>
        <p v-else-if="selectAction === 'remove'" class="gray--text mt-2">
          Selected user will be removed from all selected tasks
        </p>
        <p v-else-if="selectAction === 'replace'" class="gray--text mt-2">
          Selected user will be replaced in all selected tasks
        </p>
        <p v-else-if="selectAction === 'move'" class="gray--text mt-2">
          Selected user will be changed to preparer for all selected tasks
        </p>
      </template>
      <template v-slot:actions>
        <v-spacer />
        <v-btn
          v-if="selectAction === 'remove'"
          color="error"
          data-testid="tasklist__bulk-action-user-drawer-remove-btn"
          @click="$emit('remove')"
        >
          <span>Remove</span>
        </v-btn>
        <v-btn
          v-else-if="selectAction === 'associate'"
          color=""
          class="black white--text"
          data-testid="tasklist__bulk-action-user-drawer-associate-btn"
          @click="$emit('associate')"
        >
          <span>Add</span>
        </v-btn>
        <v-btn
          v-else-if="selectAction === 'replace'"
          color=""
          class="black white--text"
          data-testid="tasklist__bulk-action-user-drawer-replace-btn"
          @click="$emit('replace')"
        >
          <span>Replace</span>
        </v-btn>
        <v-btn
          v-else-if="selectAction === 'move'"
          color=""
          class="black white--text"
          data-testid="tasklist__bulk-action-user-drawer-move-btn"
          @click="$emit('move')"
        >
          <span>Change</span>
        </v-btn>
        <v-btn
          v-else-if="selectAction === 'update'"
          color=""
          class="black white--text"
          data-testid="tasklist__bulk-action-user-drawer-update-btn"
          @click="$emit('update-user')"
        >
          <span>Update</span>
        </v-btn>
      </template>
    </lb-drawer>
  </div>
</template>

<script>
import { drawerWidths } from '../../../../../../../styles/design-tokens.js';
import { BulkApproverEditorDialog, BulkPreparerEditorDialog } from '../../../../../components';
import { USER_ROLE } from '../../../../../utils/constants';
import {
  collectChecklistIdsFromSelectedTasks,
  collectRoleIntersectionFromCycleTasks,
  collectRoleUnionFromCycleTasks,
} from '../../../../../utils/bulkApproverEditorUtils';

export default {
  name: 'BulkActionUserDrawer',

  components: { BulkApproverEditorDialog, BulkPreparerEditorDialog },

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    isApproverEditorMode: {
      type: Boolean,
      default: false,
    },
    isPreparerEditorMode: {
      type: Boolean,
      default: false,
    },
    initialApproverTab: {
      type: String,
      default: 'approver',
      validator: (v) => ['approver', 'sign_off'].includes(v),
    },
    bulkActionUserType: {
      type: String,
      default: '',
    },
    selectedTasks: {
      type: Array,
      default: () => [],
    },
    selectAction: {
      type: String,
      default: '',
    },
    bulkActionUserId: {
      type: String,
      default: '',
    },
    changeToUser: {
      type: String,
      default: '',
    },
    actionItems: {
      type: Array,
      default: () => [],
    },
    validActiveUsers: {
      type: Array,
      default: () => [],
    },
    availableUsers: {
      type: Array,
      default: () => [],
    },
    allUsers: {
      type: Array,
      default: () => [],
    },
    saving: {
      type: Boolean,
      default: false,
    },
    cycleId: {
      type: String,
      default: '',
    },
  },

  data() {
    return { drawerWidths };
  },

  computed: {
    distinctLocationIds() {
      return [...new Set(
        (this.selectedTasks || [])
          .map((task) => task?.location)
          .filter((id) => id != null && id !== '')
          .map(String),
      )];
    },

    preparerUnionFromTasks() {
      return [...collectRoleUnionFromCycleTasks(this.selectedTasks, USER_ROLE.PREPARER)];
    },

    preparerIdsList() {
      return [...collectRoleIntersectionFromCycleTasks(this.selectedTasks, USER_ROLE.PREPARER)];
    },

    approverUnionFromTasks() {
      return [...collectRoleUnionFromCycleTasks(this.selectedTasks, USER_ROLE.APPROVER)];
    },

    signOffUnionFromTasks() {
      return [...collectRoleUnionFromCycleTasks(this.selectedTasks, USER_ROLE.SIGN_OFF_APPROVERS)];
    },

    approverIdsList() {
      return [...collectRoleIntersectionFromCycleTasks(this.selectedTasks, USER_ROLE.APPROVER)];
    },

    signOffIdsList() {
      return [...collectRoleIntersectionFromCycleTasks(this.selectedTasks, USER_ROLE.SIGN_OFF_APPROVERS)];
    },
  },

  methods: {
    onApproverEditSave({ approverIds, signOffIds }) {
      this.$emit('save-approver-edit', {
        approverIds,
        signOffIds,
        checklistIds: collectChecklistIdsFromSelectedTasks(this.selectedTasks),
      });
    },

    onPreparerEditSave({ preparerIds }) {
      this.$emit('save-preparer-edit', {
        preparerIds,
        checklistIds: collectChecklistIdsFromSelectedTasks(this.selectedTasks),
      });
    },
  },
};
</script>
